import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  Search,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { supportTicketsApi } from "../utils/api";
import { formatDateTime } from "../utils/formatters";
import Image from "./Image";

const DEFAULT_STATUS_OPTIONS = ["open", "in-progress", "resolved", "closed"];

function getTicketId(ticket) {
  return ticket?._id ?? ticket?.id ?? "";
}

function normalizeTicket(ticket) {
  const ticketId = getTicketId(ticket);
  const user = ticket?.user_id;
  const product = ticket?.product_id;

  return {
    id: ticketId,
    ticketId: ticket?.ticket_id ?? "—",
    name: ticket?.name ?? user?.name ?? "—",
    email: ticket?.email ?? user?.email ?? "—",
    mobile: user?.mobile ?? ticket?.mobile ?? "—",
    subject: ticket?.subject ?? "—",
    productName: ticket?.product_name ?? product?.product_name ?? "—",
    productType: product?.product_type ?? "—",
    qrId: product?.qrid ?? "—",
    message: ticket?.message ?? "",
    image: ticket?.image ?? "",
    status: ticket?.status ?? "open",
    createdAt: ticket?.createdAt,
    updatedAt: ticket?.updatedAt,
    raw: ticket,
  };
}

function getStatusBadge(status) {
  const value = String(status || "").toLowerCase();

  if (value === "resolved") {
    return (
      <Badge className="bg-green-600 text-white hover:bg-green-600">
        Resolved
      </Badge>
    );
  }

  if (value === "open") {
    return (
      <Badge className="bg-amber-500 text-white hover:bg-amber-500">Open</Badge>
    );
  }

  if (value === "in-progress") {
    return (
      <Badge className="bg-blue-600 text-white hover:bg-blue-600">
        In Progress
      </Badge>
    );
  }

  if (value === "closed") {
    return <Badge variant="secondary">Closed</Badge>;
  }

  return <Badge variant="outline">{status || "Unknown"}</Badge>;
}

function formatStatusLabel(status) {
  return String(status || "")
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function SupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [pendingStatuses, setPendingStatuses] = useState({});
  const [updatingIds, setUpdatingIds] = useState(new Set());

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await supportTicketsApi.getAllSupportTickets();
      const list = Array.isArray(response?.data) ? response.data : [];
      const normalized = list.map(normalizeTicket);

      setTickets(normalized);
      setPendingStatuses(
        normalized.reduce((acc, ticket) => {
          acc[ticket.id] = ticket.status;
          return acc;
        }, {})
      );
    } catch (error) {
      console.error("Error fetching support tickets:", error);
      toast.error(
        error?.message || "Failed to load support tickets. Please try again."
      );
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const statusOptions = useMemo(() => {
    const fromTickets = tickets.map((ticket) => ticket.status).filter(Boolean);
    return Array.from(new Set([...DEFAULT_STATUS_OPTIONS, ...fromTickets]));
  }, [tickets]);

  const filteredTickets = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return tickets.filter((ticket) => {
      const matchesStatus =
        statusFilter === "all" || ticket.status === statusFilter;
      const matchesSearch =
        !query ||
        [
          ticket.ticketId,
          ticket.name,
          ticket.email,
          ticket.subject,
          ticket.productName,
          ticket.qrId,
          ticket.message,
        ].some((value) => String(value || "").toLowerCase().includes(query));

      return matchesStatus && matchesSearch;
    });
  }, [tickets, searchTerm, statusFilter]);

  const total = filteredTickets.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const paginatedTickets = useMemo(() => {
    const startIndex = (currentPage - 1) * limit;
    return filteredTickets.slice(startIndex, startIndex + limit);
  }, [filteredTickets, currentPage, limit]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleLimitChange = (value) => {
    setLimit(Number(value));
    setCurrentPage(1);
  };

  const handleStatusSelection = (ticketId, value) => {
    setPendingStatuses((prev) => ({ ...prev, [ticketId]: value }));
  };

  const handleUpdateStatus = async (ticketId) => {
    const nextStatus = pendingStatuses[ticketId];
    const currentTicket = tickets.find((ticket) => ticket.id === ticketId);

    if (!ticketId || !nextStatus || !currentTicket) return;
    if (currentTicket.status === nextStatus) return;

    setUpdatingIds((prev) => new Set(prev).add(ticketId));

    try {
      const response = await supportTicketsApi.updateSupportTicketStatus(
        ticketId,
        nextStatus
      );
      const updatedTicket = normalizeTicket(response?.data ?? currentTicket.raw);

      setTickets((prev) =>
        prev.map((ticket) => (ticket.id === ticketId ? updatedTicket : ticket))
      );
      setPendingStatuses((prev) => ({ ...prev, [ticketId]: updatedTicket.status }));
      setSelectedTicket((prev) => (prev?.id === ticketId ? updatedTicket : prev));
      toast.success("Support ticket status updated successfully.");
    } catch (error) {
      console.error("Error updating support ticket status:", error);
      setPendingStatuses((prev) => ({
        ...prev,
        [ticketId]: currentTicket.status,
      }));
      toast.error(
        error?.message || "Failed to update support ticket status."
      );
    } finally {
      setUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(ticketId);
        return next;
      });
    }
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <Button
          key="first"
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
        >
          <ChevronsLeft className="w-4 h-4" />
        </Button>
      );
    }

    pages.push(
      <Button
        key="prev"
        variant="outline"
        size="sm"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
    );

    for (let page = startPage; page <= endPage; page += 1) {
      pages.push(
        <Button
          key={page}
          variant={page === currentPage ? "default" : "outline"}
          size="sm"
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Button>
      );
    }

    pages.push(
      <Button
        key="next"
        variant="outline"
        size="sm"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    );

    if (endPage < totalPages) {
      pages.push(
        <Button
          key="last"
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          <ChevronsRight className="w-4 h-4" />
        </Button>
      );
    }

    return pages;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="mb-2">Support Tickets</h1>
        <p className="text-muted-foreground">
          Review user support requests and update ticket resolution status.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Support Tickets</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full lg:max-w-sm">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by ticket, user, subject, product..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <Select value={statusFilter} onValueChange={handleStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {statusOptions.map((status) => (
                    <SelectItem key={status} value={status}>
                      {formatStatusLabel(status)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={String(limit)} onValueChange={handleLimitChange}>
                <SelectTrigger className="w-full sm:w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 / page</SelectItem>
                  <SelectItem value="25">25 / page</SelectItem>
                  <SelectItem value="50">50 / page</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ticket ID</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Update Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="h-24 text-center">
                      Loading support tickets...
                    </TableCell>
                  </TableRow>
                ) : paginatedTickets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="h-24 text-center">
                      No support tickets found.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedTickets.map((ticket) => {
                    const selectedStatus =
                      pendingStatuses[ticket.id] ?? ticket.status;
                    const isUpdating = updatingIds.has(ticket.id);

                    return (
                      <TableRow key={ticket.id}>
                        <TableCell className="font-medium">
                          {ticket.ticketId}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{ticket.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {ticket.email}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[220px]">
                          <p className="truncate" title={ticket.subject}>
                            {ticket.subject}
                          </p>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{ticket.productName}</p>
                            <p className="text-sm text-muted-foreground">
                              QR: {ticket.qrId}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(ticket.status)}</TableCell>
                        <TableCell>{formatDateTime(ticket.createdAt)}</TableCell>
                        <TableCell>
                          <div className="flex min-w-[220px] items-center gap-2">
                            <Select
                              value={selectedStatus}
                              onValueChange={(value) =>
                                handleStatusSelection(ticket.id, value)
                              }
                              disabled={isUpdating}
                            >
                              <SelectTrigger className="w-[140px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {statusOptions.map((status) => (
                                  <SelectItem key={status} value={status}>
                                    {formatStatusLabel(status)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              size="sm"
                              onClick={() => handleUpdateStatus(ticket.id)}
                              disabled={
                                isUpdating || selectedStatus === ticket.status
                              }
                            >
                              {isUpdating ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                "Update"
                              )}
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedTicket(ticket)}
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {paginatedTickets.length} of {total} tickets
            </p>
            <div className="flex items-center gap-2">{renderPagination()}</div>
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={!!selectedTicket}
        onOpenChange={(open) => {
          if (!open) setSelectedTicket(null);
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {selectedTicket?.ticketId || "Support Ticket Details"}
            </DialogTitle>
          </DialogHeader>

          {selectedTicket && (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-lg border p-4">
                  <p className="text-sm text-muted-foreground">User</p>
                  <p className="font-medium">{selectedTicket.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedTicket.email}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {selectedTicket.mobile}
                  </p>
                </div>

                <div className="rounded-lg border p-4">
                  <p className="text-sm text-muted-foreground">Product</p>
                  <p className="font-medium">{selectedTicket.productName}</p>
                  <p className="text-sm text-muted-foreground">
                    Type: {selectedTicket.productType}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    QR ID: {selectedTicket.qrId}
                  </p>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-lg border p-4">
                  <p className="text-sm text-muted-foreground">Subject</p>
                  <p className="font-medium">{selectedTicket.subject}</p>
                </div>

                <div className="rounded-lg border p-4">
                  <p className="text-sm text-muted-foreground">Status</p>
                  <div className="mt-1">{getStatusBadge(selectedTicket.status)}</div>
                  <p className="mt-3 text-sm text-muted-foreground">
                    Created: {formatDateTime(selectedTicket.createdAt)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Updated: {formatDateTime(selectedTicket.updatedAt)}
                  </p>
                </div>
              </div>

              <div className="rounded-lg border p-4">
                <p className="text-sm text-muted-foreground">Message</p>
                <p className="mt-2 whitespace-pre-wrap text-sm">
                  {selectedTicket.message || "No message provided."}
                </p>
              </div>

              <div className="rounded-lg border p-4">
                <p className="mb-3 text-sm text-muted-foreground">
                  Attached Image
                </p>
                {selectedTicket.image ? (
                  <Image
                    fileId={selectedTicket.image}
                    alt={selectedTicket.subject}
                    className="h-72 w-full rounded-lg object-cover"
                  />
                ) : (
                  <div className="flex h-72 items-center justify-center rounded-lg bg-muted text-sm text-muted-foreground">
                    No image attached.
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <Select
                  value={pendingStatuses[selectedTicket.id] ?? selectedTicket.status}
                  onValueChange={(value) =>
                    handleStatusSelection(selectedTicket.id, value)
                  }
                  disabled={updatingIds.has(selectedTicket.id)}
                >
                  <SelectTrigger className="w-full sm:w-[220px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((status) => (
                      <SelectItem key={status} value={status}>
                        {formatStatusLabel(status)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  onClick={() => handleUpdateStatus(selectedTicket.id)}
                  disabled={
                    updatingIds.has(selectedTicket.id) ||
                    (pendingStatuses[selectedTicket.id] ?? selectedTicket.status) ===
                      selectedTicket.status
                  }
                >
                  {updatingIds.has(selectedTicket.id) ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Update Ticket Status"
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
